import { ComposedChart, Line, ResponsiveContainer, Scatter, XAxis, YAxis } from 'recharts';
import { resolveToken } from '../../utils/resolveToken';

export interface TransferCurvePoint {
  x: number;
  y: number;
}

/**
 * The methodology's transfer curve, rendered per SPEC.md: two colours only —
 * the smooth curve and the patient's plotted value — with the rubric's flat
 * steps kept visible as a dashed reference. The chart's whole argument is
 * showing what the dashed cliffs would have said vs. what the curve says, so
 * the dashed line is not optional decoration.
 *
 * No band colour here — the y-axis is a continuous 0–100 score, not a band;
 * colouring it would reimpose the severity cliffs the methodology exists to
 * remove.
 *
 * Wired to the real curves: the anchors come from the scorer's config via
 * /v1/clinical/organ-health/method-curves, and the patient point is the raw
 * value + real sub-score persisted on the score. The transfer function is
 * piecewise-linear (interp between knots), so the curve is drawn linear and
 * the plotted point lands exactly on the line.
 */
export function TransferCurveChart({
  curve,
  anchors,
  patientValue,
  xLabel,
  yLabel,
  yDomain = [0, 100],
  yTicks = [0, 50, 100],
  xScale = 'linear',
  xDomain = ['dataMin', 'dataMax'],
  xTicks,
}: {
  /** The smooth transfer (or penalty) curve. */
  curve: TransferCurvePoint[];
  /** The rubric's flat-step reference points, shown dashed; omit for penalty curves. */
  anchors?: TransferCurvePoint[];
  /** The patient's plotted value; null when the raw value wasn't stored with
   * the score — the curve still renders, without a dot. */
  patientValue: TransferCurvePoint | null;
  xLabel: string;
  /** Optional rotated y-axis label; omitted for compact small-multiples. */
  yLabel?: string;
  /** Y range + ticks (default the 0–100 score scale; penalties pass their own). */
  yDomain?: [number, number];
  yTicks?: number[];
  /** X scale — 'log' for wide-range tests like UACR. */
  xScale?: 'linear' | 'log';
  xDomain?: [number | string, number | string];
  /** Explicit x tick values — needed for a log axis so ticks are clean, not raw samples. */
  xTicks?: number[];
}) {
  const fmtTick = (v: number) => (Number.isInteger(v) ? String(v) : String(Number(v.toFixed(1))));
  const curveColor = resolveToken('--chart-curve', '#a3a3a3');
  const anchorColor = resolveToken('--chart-anchor', '#d4d4d4');
  const markerColor = resolveToken('--chart-marker', '#0284c7');
  const gridColor = resolveToken('--chart-grid', '#f0f0f0');
  const axisColor = resolveToken('--chart-axis', '#d0d0d0');
  const ringColor = resolveToken('--surface-raised', '#ffffff');

  const label = patientValue
    ? `Transfer curve for ${xLabel}: patient value ${patientValue.x} maps to a score of ${patientValue.y}.`
    : `Transfer curve for ${xLabel}; the patient's recorded value was not stored with this score.`;

  return (
    <div className="h-[160px] w-full" role="img" aria-label={label}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart margin={{ top: 8, right: 8, bottom: 4, left: yLabel ? -8 : 0 }}>
          <XAxis
            dataKey="x"
            type="number"
            scale={xScale}
            domain={xDomain}
            ticks={xTicks}
            tickFormatter={fmtTick}
            allowDataOverflow={xScale === 'log'}
            tick={{ fontSize: 10, fill: axisColor }}
            tickLine={{ stroke: gridColor }}
            axisLine={{ stroke: gridColor }}
            label={{ value: xLabel, position: 'insideBottom', offset: -2, fontSize: 10, fill: axisColor }}
          />
          <YAxis
            dataKey="y"
            type="number"
            domain={yDomain}
            ticks={yTicks}
            tick={{ fontSize: 10, fill: axisColor }}
            tickLine={{ stroke: gridColor }}
            axisLine={{ stroke: gridColor }}
            width={30}
            label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', fontSize: 10, fill: axisColor } : undefined}
          />
          {anchors ? (
            <Line
              data={anchors}
              dataKey="y"
              stroke={anchorColor}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              isAnimationActive={false}
              type="stepAfter"
            />
          ) : null}
          <Line
            data={curve}
            dataKey="y"
            stroke={curveColor}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            dot={false}
            isAnimationActive={false}
            type="linear"
          />
          {patientValue ? (
            <Scatter
              data={[patientValue]}
              dataKey="y"
              shape={(props: { cx?: number; cy?: number }) => (
                <circle cx={props.cx} cy={props.cy} r={5} fill={markerColor} stroke={ringColor} strokeWidth={2} />
              )}
            />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
